import { useQuery } from '@tanstack/react-query';
import { turbinesApi } from '@/services/api';
import { logApiError } from '@/services/api/utils';
import { FailureFilters } from '@/types';
import { filterOptionsService } from '@/services/api/filterOptions';

// Query keys for cache management
export const turbineQueryKeys = {
    all: ['turbines'] as const,
    lists: () => [...turbineQueryKeys.all, 'list'] as const,
    list: (filters: FailureFilters) => [...turbineQueryKeys.lists(), filters] as const,
    listLegacy: (filters: Record<string, unknown>) =>
        [...turbineQueryKeys.lists(), 'legacy', filters] as const,
    details: () => [...turbineQueryKeys.all, 'detail'] as const,
    detail: (id: string) => [...turbineQueryKeys.details(), id] as const,
    filterOptions: () => [...turbineQueryKeys.all, 'filterOptions'] as const,
    weibullData: (filters: FailureFilters) => [...turbineQueryKeys.all, 'weibull', filters] as const,
    statistics: (filters: FailureFilters) =>
        [...turbineQueryKeys.all, 'statistics', filters] as const,
} as const;


/**
 * Hook to fetch Turbine data with multi-select filtering and pagination
 * Enhanced with proper error handling and logging
 */
export function useTurbineForPopulation(filters: FailureFilters) {
    return useQuery({
        queryKey: turbineQueryKeys.list(filters),
        queryFn: async () => {
            try {
                filters = await validateFilterOptionsSet(filters);
                return await turbinesApi.getTurbineByFilterList(filters);
            } catch (error) {
                logApiError(error, 'useFailures');
                throw error;
            }
        },
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000, // Use cacheTime for v4
    });
}

/**
 * Checks to see if at least one filter is populated.
 * If not, populates site filter with all sites so all turbines are returned
 * @param filter 
 * @returns 
 */
async function validateFilterOptionsSet(filter: FailureFilters): Promise<FailureFilters> {
    let empty = true;
    const turbineFilterKey = ["sites", "turbineModels"];
    for (const key in filter) {
        if (filter[key].length > 0 && turbineFilterKey.includes(key)) {
            empty = false;
            break;
        }
    }

    if (empty) {
        //populate site with all sites. This will ensure all turbines are returned.
        filter.sites = await filterOptionsService.getSitesOnly();
    }
    return filter;
}